'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import type { RoleUtilisateur } from '@/lib/types/database';

export interface UserProfile {
  id: string;
  nom: string;
  email: string;
  role: RoleUtilisateur;
  code_acces: string | null;
  statut: string;
  created_at: string;
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>;
  signInWithAccessCode: (email: string, code: string) => Promise<{ user: User | null; session: Session | null }>;
  signOut: () => Promise<void>;
  getUserProfile: () => Promise<UserProfile | null>;
  getRoleRedirect: (role: RoleUtilisateur) => string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const getRoleRedirect = (role: RoleUtilisateur): string => {
  switch (role) {
    case 'super_admin':
      return '/';
    case 'formateur_lead': case'formateur_appui':
      return '/tableau-de-bord-formateur';
    case 'manager':
      return '/tableau-de-bord-manager';
    case 'apprenant':
      return '/tableaux-de-bord';
    default:
      return '/';
  }
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string, userEmail?: string): Promise<UserProfile | null> => {
    try {
      // Primary lookup: by auth user ID
      const { data, error } = await supabase
        .from('utilisateurs')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) return data as UserProfile;

      // Fallback: if no row found by ID (e.g. super_admin inserted with a different UUID),
      // try matching by email — requires the caller to pass the user's email
      if (userEmail) {
        const { data: dataByEmail, error: emailError } = await supabase
          .from('utilisateurs')
          .select('*')
          .eq('email', userEmail)
          .single();
        if (!emailError && dataByEmail) return dataByEmail as UserProfile;
      }

      return null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id, session.user.email);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        const p = await fetchProfile(session.user.id, session.user.email);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithAccessCode = async (email: string, code: string) => {
    // Verify the access code against the utilisateurs table
    const { data: utilisateur, error: lookupError } = await supabase
      .from('utilisateurs')
      .select('id, email, code_acces, role, statut')
      .eq('email', email)
      .eq('code_acces', code)
      .eq('role', 'apprenant')
      .single();

    if (lookupError || !utilisateur) {
      throw new Error('Code d\'accès invalide ou e-mail incorrect.');
    }

    if (utilisateur.statut === 'cree') {
      // First login — sign in with the code as password (set during import)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password: code,
      });
      if (error) throw new Error('Impossible de se connecter. Contactez votre formateur.');
      return data;
    }

    // Subsequent logins — sign in with password = code
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password: code,
    });
    if (error) throw new Error('Code d\'accès invalide. Vérifiez votre e-mail.');
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signIn,
    signInWithAccessCode,
    signOut,
    getUserProfile,
    getRoleRedirect,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
